/**
 * Co-op online — agregação de inputs no host e montagem do snapshot (§13.4).
 *
 * O core já resolve TODA a regra de co-op (pool de vidas, espectador, revive
 * na ponte, estoque de depósito) e há teste provando que duas instâncias de 4
 * jogadores concordam através de mortes e revives. Então o que falta para o
 * co-op online não é regra: é conseguir os inputs dos outros no host, a tempo.
 *
 * É disto que este arquivo trata, e por isso ele é livre de Phaser e de rede:
 * a parte difícil é decidir o que fazer quando o input NÃO chega, e isso se
 * testa sem socket nenhum.
 *
 * Papéis (§13.1): o host simula e é a autoridade; o guest manda input e recebe
 * estado. Aliado é interpolado, nunca predito — prever o avião do outro produz
 * correção visível toda vez que ele muda de direção.
 */

import { NEUTRAL_INPUT, packInput, unpackInput, type Input, type Snapshot } from '@pixelraid/core'

/**
 * Por quantos ticks o último input de um guest continua valendo quando nada
 * novo chega.
 *
 * Zero seria errado: um pacote perdido faria o avião do parceiro engasgar a
 * cada oscilação de rede, porque "sem notícia" viraria "solta tudo". Infinito
 * também: guest que caiu ficaria voando com o acelerador preso para sempre,
 * consumindo combustível e morrendo sozinho — pior que parar.
 *
 * 12 ticks são 200 ms a 60 Hz: cobre jitter e uma perda ocasional, e ainda
 * assim solta os controles rápido o bastante para o parceiro não achar que
 * está sendo pilotado por um fantasma.
 */
const STALE_TICKS = 12

interface SlotInput {
  byte: number
  seq: number
  /** Tick do HOST em que este input foi aceito — não o tick do guest. */
  seenAt: number
}

/**
 * Junta os inputs dos guests para o host montar o array de um tick.
 *
 * Guarda um por slot, não uma fila: co-op não precisa de rollback. Se dois
 * inputs do mesmo guest chegarem entre dois ticks, o mais novo simplesmente
 * vence — o antigo já não descreve o que o dedo dele está fazendo.
 */
export class CoopInputs {
  private readonly slots = new Map<number, SlotInput>()

  /**
   * Registra input de um guest. `seq` VELHO é descartado: a rede reordena, e
   * aceitar um pacote atrasado faria o avião do parceiro voltar no tempo.
   */
  receive(slot: number, seq: number, byte: number, hostTick: number): boolean {
    const atual = this.slots.get(slot)
    if (atual !== undefined && seq <= atual.seq) return false
    this.slots.set(slot, { byte, seq, seenAt: hostTick })
    return true
  }

  /** Inputs do tick: o local no slot 0, os guests no slot deles. */
  forTick(localInput: Input, playerCount: number, hostTick: number): Input[] {
    const inputs: Input[] = new Array<Input>(playerCount).fill(NEUTRAL_INPUT)
    inputs[0] = localInput
    for (let slot = 1; slot < playerCount; slot++) {
      const guardado = this.slots.get(slot)
      if (guardado === undefined) continue
      // Silêncio longo demais solta os controles — ver STALE_TICKS.
      if (hostTick - guardado.seenAt > STALE_TICKS) continue
      inputs[slot] = unpackInput(guardado.byte)
    }
    return inputs
  }

  /** Último `seq` aceito por slot, para o `ackSeq` do snapshot (§13.6). */
  acks(playerCount: number): number[] {
    const acks: number[] = new Array<number>(playerCount).fill(-1)
    for (let slot = 1; slot < playerCount; slot++) {
      acks[slot] = this.slots.get(slot)?.seq ?? -1
    }
    return acks
  }

  /** Guest saiu: esquecer o input dele evita que ele "continue voando". */
  forget(slot: number): void {
    this.slots.delete(slot)
  }
}

/**
 * Profundidade de previsão do guest, em ticks, a partir do ida-e-volta medido.
 *
 * O guest desenha o último tick AUTORITATIVO, e esse tick é velho de duas
 * parcelas SOMADAS: o trânsito do pacote que o trouxe (meia volta) e a fila do
 * amortecedor de jitter. Para o próprio avião parecer responder na hora, a
 * previsão tem de cobrir as duas.
 *
 * O valor antigo era fixo em 3 ticks — o tamanho da fila, e só. Em partida
 * local isso basta, porque o trânsito é ~0, e foi por isso que passou nos
 * testes sem ninguém notar; num jogo pela internet sobrava a viagem inteira
 * descoberta. Com 80 ms de ida-e-volta faltavam ~40 ms de resposta; com
 * 150 ms, ~75 ms.
 *
 * `rttMs` nulo (nenhuma amostra ainda) cai no piso: cobrir a fila é a melhor
 * aposta honesta sem medida, e é exatamente o comportamento anterior.
 *
 * Função pura de propósito — é aritmética de latência, e aritmética se prova
 * com tabela, não abrindo duas janelas de navegador.
 */
export function predictTicks(
  rttMs: number | null,
  opts: { bufferTicks: number; tickMs: number; min: number; max: number },
): number {
  if (rttMs === null || !Number.isFinite(rttMs) || rttMs < 0) return opts.min
  const alvo = Math.ceil((rttMs / 2 + opts.bufferTicks * opts.tickMs) / opts.tickMs)
  return Math.min(opts.max, Math.max(opts.min, alvo))
}

/** Estado mínimo de um avião que o host precisa expor. */
export interface HostPlane {
  readonly x: number
  readonly yOffset: number
  readonly state: string
  readonly fuel: number
}

/**
 * Monta o snapshot autoritativo. Só o que o guest não consegue derivar: o
 * mundo não trafega (ele nasce da seed), e entidade tem ID determinístico.
 */
export function buildSnapshot(
  tick: number,
  scrollDist: number,
  planes: readonly HostPlane[],
  acks: readonly number[],
): Snapshot {
  return {
    tick,
    scrollDist,
    planes: planes.map((p, slot) => ({
      slot,
      x: p.x,
      yOffset: p.yOffset,
      state: p.state as never,
      fuel: p.fuel,
    })),
    ackSeq: [...acks],
  }
}

/**
 * Numerador de sequência do guest. Cresce sempre, e é o que permite ao host
 * descartar pacote atrasado sem precisar de relógio comum.
 */
export class GuestInputSender {
  private seq = 0

  /** Próximo envelope de input, ou `null` se nada mudou desde o último. */
  next(input: Input, forcar = false): { seq: number; b: number } | null {
    const b = packInput(input)
    // Input idêntico ao anterior não precisa viajar: o host repete o último
    // por STALE_TICKS. Isso derruba o tráfego de voo reto quase a zero.
    if (!forcar && this.ultimo === b) return null
    this.ultimo = b
    this.seq++
    return { seq: this.seq, b }
  }

  private ultimo = -1
}

/**
 * Linha do tempo de inputs autoritativos — o que o guest simula.
 *
 * ARQUITETURA: pela rede passam INPUTS, não estado. O core é determinístico e
 * há teste provando que duas instâncias de 4 jogadores concordam através de
 * mortes e revives; então, com o mesmo conjunto de inputs por tick, host e
 * guest produzem o mesmo mundo sem trafegar um byte de terreno, entidade ou
 * bala. Um pacote é `tick + um byte por jogador`.
 *
 * A REGRA QUE NÃO SE QUEBRA: o guest NUNCA inventa input que não chegou.
 * Inventar parece gentil — evita o engasgo — mas produz dessincronia
 * silenciosa: os dois passam a jogar mundos diferentes achando que jogam o
 * mesmo, e o erro só aparece minutos depois, como avião morrendo em nada. Com
 * transporte confiável e ordenado, buraco não é perda: é conexão quebrada. O
 * certo é travar visivelmente e deixar a camada de cima decidir.
 */
export class CoopTimeline {
  private readonly buffer = new Map<number, number[]>()
  private proximo = 0
  private comecou = false

  /**
   * @param bufferTicks Ticks acumulados antes de começar a tocar. É o
   * amortecedor de jitter: sem ele, a primeira variação de rede já engasga.
   * 3 ticks são 50 ms — atraso que não se sente, folga que se usa.
   */
  constructor(private readonly bufferTicks = 3) {}

  /** Host: empacota o conjunto autoritativo de um tick. */
  static publish(tick: number, inputs: readonly Input[]): { t: number; b: number[] } {
    return { t: tick, b: inputs.map((i) => packInput(i)) }
  }

  /** Guest: guarda um pacote. Tick já consumido é descartado. */
  receive(packet: { t: number; b: readonly number[] }): void {
    if (packet.t < this.proximo) return
    this.buffer.set(packet.t, [...packet.b])
  }

  /**
   * Guest: inputs do próximo tick, ou `null` se ainda não dá para simular.
   * `null` significa ESPERE — nunca "siga com o que tiver".
   */
  next(): Input[] | null {
    if (!this.comecou) {
      if (this.buffer.size < this.bufferTicks) return null
      this.comecou = true
    }
    const bytes = this.buffer.get(this.proximo)
    if (bytes === undefined) return null
    this.buffer.delete(this.proximo)
    this.proximo++
    return bytes.map((b) => unpackInput(b))
  }

  /** Tick que está faltando para destravar. Diagnóstico da camada de cima. */
  get esperando(): number {
    return this.proximo
  }

  /** Quantos ticks já chegaram e aguardam simulação. */
  get pendentes(): number {
    return this.buffer.size
  }
}
